/**
 * REST client for the tutoring layer: teacher/student profiles, the
 * many-to-many teacher-student links, tasks and their per-student
 * assignments, and the owner-only payment snapshot.
 *
 * Split from `api.ts` for the same reason `authApi.ts` is — a distinct
 * concern that only the "Ученики" / "Мои задачи" views and the calendar's
 * task overlay touch.
 */
import type { MemberRoleSlug } from '@kanvas/board-core';
import { request } from './api';

/** One directory of roles for both ends of the wire — see `board-core/roles`. */
export type MemberKind = MemberRoleSlug | null;

export interface RosterMember {
  userId: string;
  orgId: string;
  role: 'admin' | 'member';
  kind: MemberKind;
  name: string;
  email: string;
  color: string;
  paid: boolean;
  lessonsCount: number | null;
  paymentAmount: number | null;
  /** Epoch ms. Auto-stamped when `paid` is first ticked, editable after. */
  paidAt: number | null;
  /** Populated when `kind === 'student'`. */
  teacherIds: string[];
  /** Populated when `kind === 'teacher'`. */
  studentIds: string[];
}

export interface StudentSummary {
  userId: string;
  name: string;
  email: string;
  color: string;
}

/** A board shown on a person's roster profile. */
export interface PersonBoard {
  id: string;
  name: string;
  orgId: string;
  createdBy: string | null;
  blockId: string | null;
  groupId?: string | null;
  updatedAt: number;
  /** Whether this person created the board or was invited onto it. */
  relation: 'owner' | 'member';
}

export interface TaskAttachment {
  id: string;
  url: string;
  name: string;
  mime: string;
  bytes: number;
}

export interface TaskRecord {
  id: string;
  orgId: string;
  teacherId: string;
  /** School subject, e.g. «Алгебра». Empty when the org does not use them. */
  subject: string;
  title: string;
  description: string;
  dueAt: string | null;
  attachments: TaskAttachment[];
  createdAt: number;
  updatedAt: number;
}

export interface TaskAssignmentView extends StudentSummary {
  /** Accepted by whoever checks the work — not the student's own tick. */
  done: boolean;
  doneAt: number | null;
  /** The student ticked «сдано» themselves; it waits for review until accepted. */
  handedIn?: boolean;
  submissionAttachments: TaskAttachment[];
  boardId: string | null;
  boardName: string | null;
  submittedAt: number | null;
  /** True when files, a board or a done tick are present. */
  hasSubmission: boolean;
  /** Files, a board or the student's tick are in, waiting for the teacher to accept. */
  needsReview: boolean;
}

/** A task as whoever set it sees it, with every targeted student's progress. */
export interface TeacherTask extends TaskRecord {
  /** Who set it — the owner's list mixes their own tasks with the teachers'. */
  teacherName: string | null;
  assignments: TaskAssignmentView[];
}

/** One student's homework, as their profile card lists it. */
export interface StudentTask extends TeacherTask {
  /** That student's own completion, rather than the whole task's. */
  done: boolean;
  doneAt: number | null;
}

/** One assignment as the assigned student sees it — their own task list. */
export interface MyTaskItem {
  assignmentId: string;
  /** The student's own «сдано» tick (or the teacher's acceptance). */
  done: boolean;
  /** The teacher accepted it; `done` without this reads as «на проверке». */
  accepted?: boolean;
  doneAt: number | null;
  submissionAttachments: TaskAttachment[];
  boardId: string | null;
  boardName: string | null;
  submittedAt: number | null;
  hasSubmission: boolean;
  task: TaskRecord;
  teacher: StudentSummary | null;
}

export interface TaskInput {
  subject: string;
  title: string;
  description: string;
  dueAt: string | null;
  studentIds: string[];
  attachments: TaskAttachment[];
}

/** One assignment row as the server returns it after a change. */
export interface AssignmentPatchResult {
  id: string;
  taskId: string;
  studentId: string;
  /** The caller's view: their own tick for the student, acceptance for a teacher. */
  done: boolean;
  accepted: boolean;
  needsReview: boolean;
  doneAt: number | null;
  submissionAttachments: TaskAttachment[];
  boardId: string | null;
  boardName: string | null;
  submittedAt: number | null;
}

/**
 * A deadline as the month grid needs it: enough to draw the chip without a
 * second request — subject, who it is for, and whether it is done.
 */
export interface CalendarTaskDue {
  id: string;
  subject: string;
  title: string;
  dueAt: string;
  teacherId: string;
  teacherName: string | null;
  students: { userId: string; name: string; color: string; done: boolean; needsReview: boolean }[];
  /** The viewer's own completion, or whether every visible student is done. */
  done: boolean | null;
  /** At least one visible student has handed work in that is not yet accepted. */
  awaitingReview: boolean;
  /** Whether this account may drag the deadline to another day. */
  mayReschedule: boolean;
}

const json = (body: unknown): RequestInit['body'] => JSON.stringify(body);

export const lmsApi = {
  // ---- roster (admin) -----------------------------------------------------
  listRoster: (orgId: string) => request<RosterMember[]>(`/orgs/${orgId}/roster`),
  setMemberKind: (orgId: string, userId: string, kind: MemberKind) =>
    request<RosterMember>(`/orgs/${orgId}/members/${userId}/kind`, {
      method: 'PATCH',
      body: json({ kind }),
    }),
  setMemberPayment: (
    orgId: string,
    userId: string,
    patch: {
      paid?: boolean;
      lessonsCount?: number | null;
      paymentAmount?: number | null;
      paidAt?: number | null;
    },
  ) =>
    request<RosterMember>(`/orgs/${orgId}/members/${userId}/payment`, {
      method: 'PATCH',
      body: json(patch),
    }),
  setStudentTeachers: (orgId: string, studentId: string, teacherIds: string[]) =>
    request<{ studentId: string; teacherIds: string[] }>(
      `/orgs/${orgId}/students/${studentId}/teachers`,
      { method: 'PATCH', body: json({ teacherIds }) },
    ),
  setTeacherStudents: (orgId: string, teacherId: string, studentIds: string[]) =>
    request<{ teacherId: string; studentIds: string[] }>(
      `/orgs/${orgId}/teachers/${teacherId}/students`,
      { method: 'PATCH', body: json({ studentIds }) },
    ),

  // ---- a teacher's own roster ----------------------------------------------
  myStudents: (orgId: string) => request<StudentSummary[]>(`/orgs/${orgId}/my-students`),
  /** One student's homework, for their profile card. */
  studentTasks: (orgId: string, studentId: string) =>
    request<StudentTask[]>(`/orgs/${orgId}/students/${studentId}/tasks`),
  /** Boards a roster member created or was invited to (viewer-filtered). */
  userBoards: (orgId: string, userId: string) =>
    request<PersonBoard[]>(`/orgs/${orgId}/users/${userId}/boards`),

  // ---- tasks (teacher) ------------------------------------------------------
  listTasks: (orgId: string) => request<TeacherTask[]>(`/orgs/${orgId}/tasks`),
  createTask: (orgId: string, input: TaskInput) =>
    request<TeacherTask>(`/orgs/${orgId}/tasks`, { method: 'POST', body: json(input) }),
  updateTask: (orgId: string, taskId: string, patch: Partial<TaskInput>) =>
    request<TeacherTask>(`/orgs/${orgId}/tasks/${taskId}`, { method: 'PATCH', body: json(patch) }),
  deleteTask: (orgId: string, taskId: string) =>
    request<{ ok: true }>(`/orgs/${orgId}/tasks/${taskId}`, { method: 'DELETE' }),

  // ---- a student's own assignments -----------------------------------------
  myTasks: (orgId: string) => request<MyTaskItem[]>(`/orgs/${orgId}/my-tasks`),
  setAssignmentDone: (orgId: string, taskId: string, studentId: string, done: boolean) =>
    request<AssignmentPatchResult>(`/orgs/${orgId}/tasks/${taskId}/assignments/${studentId}`, {
      method: 'PATCH',
      body: json({ done }),
    }),
  submitAssignment: (
    orgId: string,
    taskId: string,
    studentId: string,
    patch: {
      done?: boolean;
      submissionAttachments?: TaskAttachment[];
      boardId?: string | null;
    },
  ) =>
    request<AssignmentPatchResult>(`/orgs/${orgId}/tasks/${taskId}/assignments/${studentId}`, {
      method: 'PATCH',
      body: json(patch),
    }),
  createSolutionBoard: (orgId: string, taskId: string, studentId: string) =>
    request<{
      board: { id: string; name: string; orgId: string };
      assignment: {
        boardId: string | null;
        boardName: string | null;
        submissionAttachments: TaskAttachment[];
        done: boolean;
        submittedAt: number | null;
      };
    }>(`/orgs/${orgId}/tasks/${taskId}/assignments/${studentId}/board`, {
      method: 'POST',
    }),

  // ---- calendar overlay -------------------------------------------------
  tasksCalendar: (orgId: string, from: string, to: string) =>
    request<CalendarTaskDue[]>(
      `/orgs/${orgId}/tasks/calendar?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    ),

  // ---- attachments --------------------------------------------------------
  async uploadTaskAttachment(file: File): Promise<TaskAttachment> {
    const form = new FormData();
    form.append('file', file);
    return request<TaskAttachment>('/task-attachments', { method: 'POST', body: form });
  },

  // ---- groups -------------------------------------------------------------
  listGroups: (orgId: string) => request<GroupView[]>(`/orgs/${orgId}/groups`),
  createGroup: (orgId: string, name: string, curatorIds: string[] = []) =>
    request<GroupView>(`/orgs/${orgId}/groups`, {
      method: 'POST',
      body: json({ name, curatorIds }),
    }),
  getGroup: (groupId: string) => request<GroupView>(`/groups/${groupId}`),
  updateGroup: (
    groupId: string,
    patch: {
      name?: string;
      curatorIds?: string[];
      teacherIds?: string[];
      studentIds?: string[];
    },
  ) => request<GroupView>(`/groups/${groupId}`, { method: 'PATCH', body: json(patch) }),
  deleteGroup: (groupId: string) =>
    request<{ ok: true }>(`/groups/${groupId}`, { method: 'DELETE' }),
  createGroupBoard: (groupId: string, name?: string) =>
    request<GroupBoardSummary>(`/groups/${groupId}/boards`, {
      method: 'POST',
      body: json({ name }),
    }),
};

export interface GroupBoardSummary {
  id: string;
  name: string;
  updatedAt: number;
  thumbnail: string | null;
}

export interface GroupMemberView {
  userId: string;
  name: string;
  email: string;
  color: string;
  kind: MemberKind;
  inGroupAs: 'curator' | 'teacher' | 'student';
  /** True when the viewer is a student and this person is their teacher. */
  isMyTeacher?: boolean;
}

export interface GroupView {
  id: string;
  orgId: string;
  name: string;
  curatorIds: string[];
  teacherIds: string[];
  studentIds: string[];
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  canManage: boolean;
  canCreateBoard: boolean;
  viewerInGroupAs: 'curator' | 'teacher' | 'student' | null;
  boards: GroupBoardSummary[];
  members: GroupMemberView[];
}
